import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Shield,
  Loader2,
  CheckCircle2,
  Tag,
  Save,
  Edit2,
  X,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchCustomerById, updateCustomer } from "@/lib/api";
import { getAuthenticatedUser, setAuthenticatedUser } from "@/lib/auth";
import { mockCurrentUser, type CurrentUser } from "@/lib/currentUser";

// ----------------------------------------------------------------------------

interface ProfiloProps {
  className?: string;
}

export function Profilo(props: ProfiloProps) {
  const { className } = props;

  const initialUser = getAuthenticatedUser() ?? mockCurrentUser;
  const [profileData, setProfileData] = useState<CurrentUser>(initialUser);
  const [draftData, setDraftData] = useState<CurrentUser>(initialUser);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load live customer details from Storeden API if ID is available
  useEffect(() => {
    let isMounted = true;

    async function loadLiveProfile() {
      const user = getAuthenticatedUser() ?? mockCurrentUser;
      if (!user.id || user.id.startsWith("user_")) {
        return;
      }

      setIsLoading(true);
      try {
        const liveRecord = await fetchCustomerById(user.id);
        if (liveRecord && isMounted) {
          const updatedUser: CurrentUser = {
            ...user,
            fullName: liveRecord.name || user.fullName,
            email: liveRecord.email || user.email,
            phone: liveRecord.phone || user.phone,
            addressLine1: liveRecord.addressLine1 || user.addressLine1,
            city: liveRecord.city || user.city,
            postalCode: liveRecord.postalCode || user.postalCode,
            province: liveRecord.province || user.province,
            country: liveRecord.country || user.country,
            taxNumber: liveRecord.taxNumber || liveRecord.code || user.taxNumber,
            notes: liveRecord.notes || user.notes,
          };
          setProfileData(updatedUser);
          setDraftData(updatedUser);
          if (Array.isArray(liveRecord.tags)) {
            setUserTags(liveRecord.tags);
          }
        }
      } catch (err) {
        console.warn("[Profilo] Failed to load live profile from Storeden:", err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadLiveProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleFieldChange(field: keyof CurrentUser, value: string) {
    setDraftData((currentValue) => ({
      ...currentValue,
      [field]: value,
    }));
  }

  function handleStartEditing() {
    setDraftData(profileData);
    setIsEditing(true);
  }

  function handleCancelEditing() {
    setDraftData(profileData);
    setIsEditing(false);
  }

  async function handleSaveProfile(e?: React.FormEvent) {
    if (e) e.preventDefault();

    if (!draftData.fullName.trim() || !draftData.email.trim()) {
      toast.error("Nome e Email sono obbligatori");
      return;
    }

    setIsSaving(true);
    try {
      // 1. If real Storeden customer ID is present, save via PUT API to Storeden
      if (draftData.id && !draftData.id.startsWith("user_")) {
        await updateCustomer(draftData.id, {
          fullName: draftData.fullName.trim(),
          email: draftData.email.trim(),
          phone: draftData.phone.trim() || undefined,
          address: draftData.addressLine1.trim() || undefined,
          city: draftData.city.trim() || undefined,
          zip: draftData.postalCode.trim() || undefined,
          state: draftData.country.trim() || undefined,
          province: draftData.province.trim() || undefined,
          taxNumber: draftData.taxNumber?.trim() || undefined,
          code: draftData.taxNumber?.trim() || undefined,
          note: draftData.notes?.trim() || undefined,
          notes: draftData.notes?.trim() || undefined,
          tags: userTags.length > 0 ? userTags : undefined,
        });
      }

      // 2. Update local state and session storage
      const words = draftData.fullName.trim().split(" ").filter(Boolean);
      const initials = words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");

      const updatedUser: CurrentUser = {
        ...draftData,
        initials: initials || draftData.initials,
      };

      setProfileData(updatedUser);
      setAuthenticatedUser(updatedUser);
      setIsEditing(false);

      toast.success("Profilo agente aggiornato con successo su Storeden");
    } catch (error) {
      console.error("[Profilo] Failed to update profile:", error);
      toast.error("Errore durante l'aggiornamento del profilo su Storeden.");
    } finally {
      setIsSaving(false);
    }
  }

  const displayedProfile = isEditing ? draftData : profileData;

  return (
    <DashboardLayout className={className}>
      <div className="w-full max-w-5xl mx-auto space-y-6">
        <PageBreadcrumb
          items={[
            { label: "Home", to: "/" },
            { label: "Profilo Agente" },
          ]}
        />

        {/* Top Profile Header Card */}
        <Card className="shadow-sm border-gray-200 overflow-hidden">
          <div className="h-28 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 flex items-start justify-between px-6 pt-5">
            <div className="flex items-center gap-2 text-white">
              <Shield className="w-5 h-5 text-[#E9E21B]" />
              <span className="text-sm font-semibold tracking-wide uppercase">
                Area Riservata Agenti B2B
              </span>
            </div>
            {profileData.agentCode && (
              <span className="bg-[#E9E21B] text-black font-bold px-3 py-1 rounded-full text-xs">
                Codice Agente: {profileData.agentCode}
              </span>
            )}
          </div>

          <CardContent className="p-6 pt-0">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="-mt-12 w-24 h-24 rounded-2xl bg-[#E9E21B] text-black font-extrabold text-3xl flex items-center justify-center shadow-lg border-4 border-white shrink-0">
                  {displayedProfile.initials || "AG"}
                </div>
                <div className="pt-1 sm:pt-0 sm:pb-1">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {displayedProfile.fullName}
                  </h1>
                  <p className="text-sm text-gray-500 font-medium flex items-center gap-1.5 mt-0.5">
                    <Mail className="w-3.5 h-3.5" />
                    {displayedProfile.email}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      disabled={isSaving}
                      onClick={handleCancelEditing}
                      className="border-gray-300"
                    >
                      <X className="w-4 h-4 mr-1.5" />
                      Annulla
                    </Button>
                    <Button
                      disabled={isSaving}
                      onClick={() => void handleSaveProfile()}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvataggio...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-1.5" />
                          Salva modifiche
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleStartEditing}
                    className="bg-black hover:bg-gray-800 text-white font-semibold"
                  >
                    <Edit2 className="w-4 h-4 mr-1.5" />
                    Modifica dati profilo
                  </Button>
                )}
              </div>
            </div>

            {/* Tags Badge list */}
            {userTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100">
                <span className="text-xs font-semibold text-gray-400 flex items-center gap-1 mr-1">
                  <Tag className="w-3.5 h-3.5" /> Tag Storeden:
                </span>
                {userTags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-100 text-gray-700 text-xs px-2.5 py-0.5 rounded-md font-mono border border-gray-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profile Details Form */}
        <Card className="shadow-sm border-gray-200 gap-0 py-5">
          <CardHeader className="px-6 pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold text-gray-900">
                  Dati Anagrafici & Sede Operativa
                </CardTitle>
                <CardDescription className="text-xs text-gray-500 mt-0.5">
                  Questi dati vengono sincronizzati direttamente con la scheda utente dell'e-commerce Storeden.
                </CardDescription>
              </div>
              {isLoading && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Sincronizzazione da Storeden...
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="px-6 pt-5 pb-2">
            <form onSubmit={(e) => void handleSaveProfile(e)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome e Cognome */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="profile-fullname" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Nome e Cognome / Ragione Sociale *
                  </Label>
                  <div className="relative">
                    <Input
                      id="profile-fullname"
                      value={draftData.fullName}
                      disabled={!isEditing}
                      onChange={(e) => handleFieldChange("fullName", e.target.value)}
                      className={!isEditing ? "bg-gray-50 text-gray-900 font-medium" : "bg-white"}
                    />
                    {!isEditing && <User className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />}
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="profile-email" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Indirizzo Email *
                  </Label>
                  <div className="relative">
                    <Input
                      id="profile-email"
                      type="email"
                      value={draftData.email}
                      disabled={!isEditing}
                      onChange={(e) => handleFieldChange("email", e.target.value)}
                      className={!isEditing ? "bg-gray-50 text-gray-900 font-medium" : "bg-white"}
                    />
                    {!isEditing && <Mail className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />}
                  </div>
                </div>

                {/* Telefono */}
                <div className="space-y-2">
                  <Label htmlFor="profile-phone" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Telefono / Cellulare
                  </Label>
                  <div className="relative">
                    <Input
                      id="profile-phone"
                      value={draftData.phone || ""}
                      placeholder="es. +39 347 1234567"
                      disabled={!isEditing}
                      onChange={(e) => handleFieldChange("phone", e.target.value)}
                      className={!isEditing ? "bg-gray-50 text-gray-900" : "bg-white"}
                    />
                    {!isEditing && <Phone className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />}
                  </div>
                </div>

                {/* Indirizzo */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="profile-address" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Indirizzo Sede (Via / Piazza / Numero)
                  </Label>
                  <div className="relative">
                    <Input
                      id="profile-address"
                      value={draftData.addressLine1 || ""}
                      placeholder="es. Via Roma, 100"
                      disabled={!isEditing}
                      onChange={(e) => handleFieldChange("addressLine1", e.target.value)}
                      className={!isEditing ? "bg-gray-50 text-gray-900" : "bg-white"}
                    />
                    {!isEditing && <MapPin className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />}
                  </div>
                </div>

                {/* Comune / Città */}
                <div className="space-y-2">
                  <Label htmlFor="profile-city" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Città / Comune
                  </Label>
                  <Input
                    id="profile-city"
                    value={draftData.city || ""}
                    placeholder="es. Milano"
                    disabled={!isEditing}
                    onChange={(e) => handleFieldChange("city", e.target.value)}
                    className={!isEditing ? "bg-gray-50 text-gray-900" : "bg-white"}
                  />
                </div>

                {/* CAP */}
                <div className="space-y-2">
                  <Label htmlFor="profile-zip" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    CAP (Codice Postale)
                  </Label>
                  <Input
                    id="profile-zip"
                    value={draftData.postalCode || ""}
                    placeholder="es. 20121"
                    disabled={!isEditing}
                    onChange={(e) => handleFieldChange("postalCode", e.target.value)}
                    className={!isEditing ? "bg-gray-50 text-gray-900" : "bg-white"}
                  />
                </div>

                {/* Provincia */}
                <div className="space-y-2">
                  <Label htmlFor="profile-province" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Provincia
                  </Label>
                  <Input
                    id="profile-province"
                    value={draftData.province || ""}
                    placeholder="es. MI"
                    disabled={!isEditing}
                    onChange={(e) => handleFieldChange("province", e.target.value)}
                    className={!isEditing ? "bg-gray-50 text-gray-900" : "bg-white"}
                  />
                </div>

                {/* Nazione / Stato */}
                <div className="space-y-2">
                  <Label htmlFor="profile-country" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Nazione / Stato
                  </Label>
                  <Input
                    id="profile-country"
                    value={draftData.country || ""}
                    placeholder="es. Italia"
                    disabled={!isEditing}
                    onChange={(e) => handleFieldChange("country", e.target.value)}
                    className={!isEditing ? "bg-gray-50 text-gray-900" : "bg-white"}
                  />
                </div>

                {/* Partita IVA / Codice Fiscale */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="profile-taxNumber" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Partita IVA / Codice Fiscale
                  </Label>
                  <Input
                    id="profile-taxNumber"
                    value={draftData.taxNumber || ""}
                    placeholder="es. 05323420280"
                    disabled={!isEditing}
                    onChange={(e) => handleFieldChange("taxNumber", e.target.value)}
                    className={!isEditing ? "bg-gray-50 text-gray-900" : "bg-white"}
                  />
                </div>

                {/* Note B2B */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="profile-notes" className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Note B2B Agente
                  </Label>
                  <textarea
                    id="profile-notes"
                    rows={3}
                    value={draftData.notes || ""}
                    placeholder="Eventuali note sul profilo agente..."
                    disabled={!isEditing}
                    onChange={(e) => handleFieldChange("notes", e.target.value)}
                    className={`w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 ${!isEditing ? "bg-gray-50 text-gray-700 cursor-default" : "bg-white"
                      }`}
                  />
                </div>
              </div>

              {isEditing && (
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSaving}
                    onClick={handleCancelEditing}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvataggio su Storeden...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        Conferma e Salva
                      </>
                    )}
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
